// Lib
import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

// Utils
import { getElementId } from '../../../../../common/elements/utils/elementPropertyUtils';

// Hooks
import { ImageModalV2Mode } from './hooks/useImageModalV2Mode';
import { useImageModalShell } from './hooks/useImageModalShell';

// Components
import ImageModalV2Content from './content/ImageModalV2Content';
import ImageModalV2EditModeContent from './content/ImageModalV2EditModeContent';
import ImageModalV2Toolbar from './toolbar/ImageModalV2Toolbar';
import ImageModalFilesizePreloader from '../ImageModalFilesizePreloader';
import FileTransferToast from '../../../attachments/FileTransferToast';

import { lowerQualityImageUrlSelector } from '../imageModalSelectors';

// Types
import { ImageModalV2Props } from './imageModalV2Types';
import { UseImageModalZoomResult } from '../hooks/useImageModalZoom';
import { ReduxState } from '../../../../types/reduxTypes';

// Styles
import './ImageModalV2.scss';

type ImageAreaContentProps = Pick<ImageModalV2Props, 'element' | 'isEditable'> & {
    mode: ImageModalV2Mode;
    lowerQualityImageUrl?: string;
    contentStyle: React.CSSProperties;
    editOverlayStyle: React.CSSProperties;
    imageWrapperStyle: React.CSSProperties;
    imageBounds: ReturnType<typeof useImageModalShell>['layout']['imageBounds'];
    captionRef: React.RefObject<HTMLDivElement>;
    imageZoom: UseImageModalZoomResult;
    editModeProps: ReturnType<typeof useImageModalShell>['editModeProps'];
};

const ImageAreaContent: React.FC<ImageAreaContentProps> = ({
    element,
    isEditable,
    mode,
    lowerQualityImageUrl,
    contentStyle,
    editOverlayStyle,
    imageWrapperStyle,
    imageBounds,
    captionRef,
    imageZoom,
    editModeProps,
}) => {
    const showViewModeTransparencyGrid =
        mode === ImageModalV2Mode.VIEW || mode === ImageModalV2Mode.UPLOAD || mode === ImageModalV2Mode.DOWNLOAD;
    const hideViewContentForEdit = mode === ImageModalV2Mode.CROP;
    const viewContent = (
        <ImageModalV2Content
            element={element}
            isEditable={isEditable}
            lowerQualityImageUrl={lowerQualityImageUrl}
            showTransparencyGrid={showViewModeTransparencyGrid}
            isHiddenForEdit={hideViewContentForEdit}
            contentStyle={contentStyle}
            imageWrapperStyle={imageWrapperStyle}
            captionRef={captionRef}
            imageZoom={imageZoom}
        />
    );

    const editContent = (
        <ImageModalV2EditModeContent
            mode={mode}
            {...editModeProps}
            contentStyle={contentStyle}
            editOverlayStyle={editOverlayStyle}
            imageWrapperStyle={imageWrapperStyle}
            imageBounds={imageBounds}
        />
    );

    switch (mode) {
        case ImageModalV2Mode.VIEW:
        case ImageModalV2Mode.UPLOAD:
        case ImageModalV2Mode.DOWNLOAD:
            return viewContent;
        case ImageModalV2Mode.DRAW:
        case ImageModalV2Mode.CROP:
        case ImageModalV2Mode.BACKGROUND:
            return (
                <>
                    {viewContent}
                    {editContent}
                </>
            );
    }
};

const ImageModalV2: React.FC<ImageModalV2Props> = (props) => {
    const { element, isEditable } = props;
    const elementId = getElementId(element);
    const lowerQualityImageUrl = useSelector((state: ReduxState) => lowerQualityImageUrlSelector(state, props));

    const {
        modalRef,
        primaryToolbarRef,
        captionRef,
        mode,
        setMode,
        isToolPanelOpen,
        layout,
        imageZoom,
        editModeProps,
        canNavigatePrevious,
        canNavigateNext,
        orderedImageElements,
        navigateToPreviousImage,
        navigateToNextImage,
        handleModalMouseDown,
        handleModalClick,
    } = useImageModalShell(props);

    const isToolbarVisible =
        !imageZoom.isZoomed && mode !== ImageModalV2Mode.UPLOAD && mode !== ImageModalV2Mode.DOWNLOAD;

    return (
        <>
            <ImageModalFilesizePreloader imagesToPreload={orderedImageElements} />
            <FileTransferToast elementId={elementId} groupId="ImageModalV2" />

            <div
                ref={modalRef}
                className={classNames(
                    'ImageModalV2',
                    `mode-${mode.toLowerCase()}`,
                    'fixed inset-0 box-border flex h-screen w-screen flex-col pointer-events-auto',
                )}
                onMouseDown={handleModalMouseDown}
                onClick={handleModalClick}
            >
                <div
                    className="ImageModalV2-image-area relative flex w-full min-h-0 flex-none items-center justify-center overflow-hidden"
                    style={layout.imageAreaStyle}
                >
                    <ImageAreaContent
                        element={element}
                        isEditable={isEditable}
                        mode={mode}
                        lowerQualityImageUrl={lowerQualityImageUrl}
                        contentStyle={layout.contentStyle}
                        editOverlayStyle={layout.editOverlayStyle}
                        imageWrapperStyle={layout.imageWrapperStyle}
                        imageBounds={layout.imageBounds}
                        captionRef={captionRef}
                        imageZoom={imageZoom}
                        editModeProps={editModeProps}
                    />
                </div>

                <ImageModalV2Toolbar
                    mode={mode}
                    element={element}
                    isEditable={isEditable}
                    canNavigatePrevious={canNavigatePrevious}
                    canNavigateNext={canNavigateNext}
                    navigateToPreviousImage={navigateToPreviousImage}
                    navigateToNextImage={navigateToNextImage}
                    setMode={setMode}
                    isToolPanelOpen={isToolPanelOpen}
                    isVisible={isToolbarVisible}
                    style={layout.toolbarStyle}
                    primaryToolbarRef={primaryToolbarRef}
                />
            </div>
        </>
    );
};

export default ImageModalV2;
